import { useEffect, useRef, useCallback } from 'react'
import { Room, ConnectionState, RoomEvent } from 'livekit-client'

import { useIsMobile } from '@/src/hooks/useIsMobile'

interface UseMobileReconnectOptions {
  room: Room | null | undefined
  enabled?: boolean
  /**
   * Вызывается, когда вкладка вернулась из фона, а соединение уже мертво
   * или зависло после bfcache. Сам хук connect не вызывает.
   */
  onReconnectNeeded: () => void
}

/**
 * Детектор возврата из фона на мобильном: сам connect не вызывает.
 *
 * Раньше хук поднимал тот же Room через room.connect, и это спорило
 * с retry в ActiveRoom, который создаёт новый Room. Теперь только
 * сообщает «нужно переподключиться».
 *
 * Особый случай — bfcache: комната может считать себя живой, хотя ICE уже мёртв.
 */
export function useMobileReconnect({
  room,
  enabled = true,
  onReconnectNeeded,
}: UseMobileReconnectOptions) {
  const wasInBackgroundRef = useRef(false)
  const delayTimeoutsRef = useRef(new Set<ReturnType<typeof setTimeout>>())
  const roomRef = useRef(room)
  const enabledRef = useRef(enabled)
  const onReconnectNeededRef = useRef(onReconnectNeeded)

  roomRef.current = room
  enabledRef.current = enabled
  onReconnectNeededRef.current = onReconnectNeeded

  const { isMobileByUserAgent, isInAppBrowser } = useIsMobile()

  /**
   * Сбрасывает отложенные проверки visibility/online.
   *
   * Если их не снять при смене Room, колбэк дёрнет retry у уже
   * выброшенного инстанса.
   */
  const clearDelayTimeouts = useCallback(() => {
    delayTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
    delayTimeoutsRef.current.clear()
  }, [])

  /**
   * Решает, звать ли retry в ActiveRoom после паузы.
   *
   * SDK в Reconnecting не трогаем: он ещё может закрыть короткое моргание.
   * Disconnected — сеть уже потеряна, SDK сдался. Connected после bfcache
   * часто ложный: вкладка была заморожена и не узнала, что ICE умер.
   */
  const requestRetryIfNeeded = useCallback((forceIfConnected: boolean) => {
    if (!enabledRef.current || roomRef.current !== room) {
      return
    }

    if (!room) {
      return
    }

    const currentState = room.state

    if (
      currentState === ConnectionState.Reconnecting ||
      currentState === ConnectionState.Connecting
    ) {
      console.log(`[MobileReconnect] State is ${currentState}, letting SDK handle it`)

      return
    }

    if (currentState === ConnectionState.Disconnected) {
      console.log('[MobileReconnect] Connection lost, asking ActiveRoom to retry')
      onReconnectNeededRef.current()

      return
    }

    if (forceIfConnected && currentState === ConnectionState.Connected) {
      console.log('[MobileReconnect] Stale connection after bfcache, asking ActiveRoom to retry')
      onReconnectNeededRef.current()
    }
  }, [room])

  /**
   * Откладывает проверку, чтобы SDK успел сам войти в Reconnecting.
   */
  const scheduleCheck = useCallback(
    (forceIfConnected: boolean, delayMs: number) => {
      const timeoutId = setTimeout(() => {
        delayTimeoutsRef.current.delete(timeoutId)
        requestRetryIfNeeded(forceIfConnected)
      }, delayMs)

      delayTimeoutsRef.current.add(timeoutId)
    },
    [requestRetryIfNeeded],
  )

  const handleVisibilityChange = useCallback(() => {
    if (!room || !enabled) {
      return
    }

    const isHidden = document.hidden || document.visibilityState === 'hidden'

    if (isHidden) {
      wasInBackgroundRef.current = true
      console.log(`[MobileReconnect] Page going to background, state: ${room.state}`)

      return
    }

    if (!wasInBackgroundRef.current) {
      return
    }

    wasInBackgroundRef.current = false
    console.log(`[MobileReconnect] Page returning from background, state: ${room.state}`)
    scheduleCheck(false, 1000)
  }, [room, enabled, scheduleCheck])

  /**
   * bfcache на iOS не всегда шлёт visibilitychange, поэтому не ждём флаг фона.
   * Соединение после разморозки почти наверняка мертво, даже если state — Connected.
   */
  const handlePageShow = useCallback(
    (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return
      }

      console.log('[MobileReconnect] Page restored from bfcache')
      wasInBackgroundRef.current = false
      scheduleCheck(true, 1000)
    },
    [scheduleCheck],
  )

  const handleOnline = useCallback(() => {
    if (!room || !enabled) {
      return
    }

    console.log('[MobileReconnect] Network came online')
    scheduleCheck(false, 2000)
  }, [room, enabled, scheduleCheck])

  useEffect(() => {
    if (!room || !enabled) {
      return
    }

    const handleConnectionStateChange = (state: ConnectionState) => {
      console.log(`[MobileReconnect] Connection state changed: ${state}`)

      if (state === ConnectionState.Connected) {
        clearDelayTimeouts()
      }
    }

    room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChange)

    return () => {
      room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChange)
    }
  }, [room, enabled, clearDelayTimeouts])

  useEffect(() => {
    if (!enabled || (!isMobileByUserAgent && !isInAppBrowser)) {
      return
    }

    console.log('[MobileReconnect] Initializing mobile reconnect detector')

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('online', handleOnline)
    window.addEventListener('focus', handleVisibilityChange)
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('focus', handleVisibilityChange)
      window.removeEventListener('pageshow', handlePageShow)
      clearDelayTimeouts()
    }
  }, [
    enabled,
    isMobileByUserAgent,
    isInAppBrowser,
    handleVisibilityChange,
    handleOnline,
    handlePageShow,
    clearDelayTimeouts,
  ])

  return {
    isMobile: isMobileByUserAgent,
    isInAppBrowser,
  }
}
